import React, { useEffect, useMemo, useState } from "react";
import Navbar from "../../Includes/Navbar/Navbar";
import Sidebar from "../../Includes/Sidebar/Sidebar";
import UserServices from "../../../UserServices/UserServices";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, AreaChart, Area } from "recharts";

const fetchAttendance = async ({ start_date, end_date, branch_id = "", gender = "", start_time = "", end_time = "", limit = 100, page = 1 }) => {
  try {
    const resp = await UserServices.getClientAttendence(
      "Member",
      "1",
      branch_id,
      {
        sdate: start_date,
        edate: end_date,
        start_time,
        end_time,
        gender,
      },
      "",
      limit,
      page
    );
    if (resp.status === 200) {
      return resp.data.data?.data || [];
    }
    return [];
  } catch (e) {
    return [];
  }
};

const formatBranch = (uid = "") => { const m = uid.match(/^([A-Za-z]+)(\d+)/); return m ? `${m[1]}-${m[2]}` : uid; };
const hourLabel = (h) => { const hour = Number(h); const suffix = hour >= 12 ? "PM" : "AM"; const v = hour % 12 || 12; return `${v}:00 ${suffix}`; };

export default function FootfallPeakReport({ initialFilters, embedded = false }) {
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [filters, setFilters] = useState(initialFilters || { start_date: today, end_date: today, branch_id: "", gender: "", start_time: "", end_time: "", limit: 25, page: 1 });
  const [raw, setRaw] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tempFilters, setTempFilters] = useState(() => ({ ...filters }));

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const multiDay = filters.start_date !== filters.end_date;
        if (!multiDay) {
          setRaw(await fetchAttendance(filters));
        } else {
          const getDateList = (startIso, endIso) => {
            const out = [];
            let cur = new Date(startIso);
            const end = new Date(endIso);
            while (cur <= end) {
              out.push(cur.toISOString().slice(0, 10));
              cur.setDate(cur.getDate() + 1);
            }
            return out;
          };
          const days = getDateList(filters.start_date, filters.end_date);
          const results = await Promise.all(
            days.map((d) => fetchAttendance({ ...filters, start_date: d, end_date: d, page: 1, limit: 1000 }))
          );
          setRaw(results.flat());
        }
      } catch (_) {} finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [filters]);

  const hourly = useMemo(() => {
    const map = new Map();
    raw.forEach((r) => {
      const h = (r.checkin_time_24h || "00:00:00").slice(0, 2);
      const br = formatBranch(r.attendee?.uid || "");
      const key = h;
      const row = map.get(key) || { hour: h, total: 0, Male: 0, Female: 0 };
      row.total += 1;
      const g = (r.gender || "").toLowerCase();
      if (g === "male") row.Male += 1;
      if (g === "female") row.Female += 1;
      row[br] = (row[br] || 0) + 1;
      map.set(key, row);
    });
    const arr = Array.from(map.values()).sort((a, b) => Number(a.hour) - Number(b.hour));
    return arr.map((d) => ({ ...d, hourLabel: hourLabel(d.hour) }));
  }, [raw]);

  const busiest = useMemo(() => {
    if (!hourly.length) return { hourLabel: "-", total: 0 };
    return hourly.reduce((a, b) => (b.total > a.total ? b : a), hourly[0]);
  }, [hourly]);

  const avgPerHour = useMemo(() => {
    if (!hourly.length) return 0;
    const sum = hourly.reduce((s, r) => s + r.total, 0);
    return Math.round((sum / hourly.length) * 10) / 10;
  }, [hourly]);

  const branches = useMemo(() => {
    const set = new Set();
    raw.forEach((r) => set.add(formatBranch(r.attendee?.uid || "")));
    return Array.from(set);
  }, [raw]);

  // Group raw by date and compute per-date hourly breakdowns (Male/Female + per branch)
  const groupedByDate = useMemo(() => {
    const map = {};
    raw.forEach((r) => {
      const d = r.date || "";
      if (!d) return;
      map[d] = map[d] || [];
      map[d].push(r);
    });
    return map;
  }, [raw]);

  const perDateHourly = useMemo(() => {
    const out = {};
    Object.keys(groupedByDate).forEach((d) => {
      const map = new Map();
      groupedByDate[d].forEach((r) => {
        const h = (r.checkin_time_24h || "00:00:00").slice(0, 2);
        const br = formatBranch(r.attendee?.uid || "");
        const row = map.get(h) || { hour: h, hourLabel: hourLabel(h), Male: 0, Female: 0 };
        const g = (r.gender || "").toLowerCase();
        if (g === "male") row.Male += 1;
        if (g === "female") row.Female += 1;
        row[br] = (row[br] || 0) + 1;
        map.set(h, row);
      });
      out[d] = Array.from(map.values()).sort((a, b) => Number(a.hour) - Number(b.hour));
    });
    return out;
  }, [groupedByDate]);

  const onTempChange = (e) => { const { name, value } = e.target; setTempFilters((f)=>({ ...f, [name]: value })); };
  const applyFilters = () => { setFilters((prev)=>({ ...prev, ...tempFilters, page: 1 })); };

  // Use API pagination if needed in details; peak renders hourly aggregation of full page
  const paged = raw;

  const content = (
    <div className="container-fluid">
      <div className="card mb-3"><div className="card-body">
        <form className="row g-2" aria-label="Footfall peak filters">
          <div className="col-12 col-md-3"><label className="form-label">Start date</label><input name="start_date" type="date" className="form-control" value={tempFilters.start_date} onChange={onTempChange} /></div>
          <div className="col-12 col-md-3"><label className="form-label">End date</label><input name="end_date" type="date" className="form-control" value={tempFilters.end_date} onChange={onTempChange} /></div>
          <div className="col-12 col-md-2"><label className="form-label">Branch</label>
            <select name="branch_id" className="form-select" value={tempFilters.branch_id} onChange={onTempChange}>
              <option value="">All</option>
              <option value="15">F-11</option>
              <option value="1">G-13</option>
            </select>
          </div>
          <div className="col-12 col-md-2"><label className="form-label">Gender</label><select name="gender" className="form-select" value={tempFilters.gender} onChange={onTempChange}><option value="">All</option><option value="Male">Male</option><option value="Female">Female</option></select></div>
          <div className="col-6 col-md-1"><label className="form-label">From</label><input name="start_time" type="time" className="form-control" value={tempFilters.start_time} onChange={onTempChange} /></div>
          <div className="col-6 col-md-1"><label className="form-label">To</label><input name="end_time" type="time" className="form-control" value={tempFilters.end_time} onChange={onTempChange} /></div>
          <div className="col-12 col-md-1 d-flex align-items-end"><button type="button" className="btn btn-primary w-100" onClick={applyFilters}>Go</button></div>
        </form>
      </div></div>

      <div className="row g-3">
        <div className="col-12 col-md-6"><div className="card h-100"><div className="card-body"><div className="text-muted">Busiest Hour</div><div className="fs-3 fw-bold">{busiest.hourLabel}</div></div></div></div>
        <div className="col-12 col-md-6"><div className="card h-100"><div className="card-body"><div className="text-muted">Average Utilization (entries/hour)</div><div className="fs-3 fw-bold">{avgPerHour}</div></div></div></div>
      </div>

      <div className="card my-3"><div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-2"><h6 className="mb-0">Entries by Hour</h6>{loading ? <small className="text-muted">Loading…</small> : null}</div>
        <div style={{ width: "100%", height: 320 }}>
          <ResponsiveContainer>
            <BarChart data={hourly} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="hourLabel" interval={0} tick={{ fontSize: 10 }} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Bar dataKey="Male" stackId="g" fill="#0088FE" />
              <Bar dataKey="Female" stackId="g" fill="#FF6384" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div></div>

      <div className="card my-3"><div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-2"><h6 className="mb-0">Hourly Usage (Area)</h6></div>
        <div style={{ width: "100%", height: 280 }}>
          <ResponsiveContainer>
            <AreaChart data={hourly} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#00C49F" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#00C49F" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="hourLabel" interval={0} tick={{ fontSize: 10 }} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Area type="monotone" dataKey="total" stroke="#00C49F" fillOpacity={1} fill="url(#colorTotal)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div></div>

      {filters.start_date === filters.end_date ? (
        <div className="card"><div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-2"><h6 className="mb-0">Detail</h6>
            <div className="d-flex gap-2">
              <button type="button" className="btn btn-outline-secondary" onClick={() => {
                const headers = ["Hour","Male","Female", ...branches];
                const rows = hourly.map(r => [r.hourLabel, r.Male||0, r.Female||0, ...branches.map(b=>r[b]||0)]);
                const csv=[headers,...rows].map(r=>r.map(v=>`"${String(v).replaceAll('"','""')}"`).join(",")).join("\n");
                const blob=new Blob([csv],{type:"text/csv;charset=utf-8;"}); const url=URL.createObjectURL(blob); const a=document.createElement("a"); a.href=url; a.download=`footfall_peak_${filters.start_date}_${filters.end_date}.csv`; a.click(); URL.revokeObjectURL(url);
              }}>CSV</button>
            </div>
          </div>
          <div className="table-responsive">
            <table className="table table-sm align-middle">
              <thead><tr><th>Hour</th><th>Male</th><th>Female</th>{branches.map(b=> (<th key={b}>{b}</th>))}</tr></thead>
              <tbody>
                {hourly.map((r, idx) => (
                  <tr key={idx}><td>{r.hourLabel}</td><td>{r.Male||0}</td><td>{r.Female||0}</td>{branches.map(b => (<td key={b}>{r[b]||0}</td>))}</tr>
                ))}
                {!hourly.length && (<tr><td colSpan={3+branches.length} className="text-center text-muted">No records</td></tr>)}
              </tbody>
            </table>
          </div>
        </div></div>
      ) : (
        <div className="row g-3">
          {Object.keys(perDateHourly).sort().map((d) => (
            <div className="col-12" key={d}>
              <div className="row g-3">
                <div className="col-12 col-lg-6">
                  <div className="card h-100"><div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-2"><h6 className="mb-0">{d.split('-').reverse().join('/')}</h6><span className="badge bg-primary">Total: {groupedByDate[d]?.length || 0}</span></div>
                    <div style={{ width: "100%", height: 280 }}>
                      <ResponsiveContainer>
                        <BarChart data={perDateHourly[d]} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="hourLabel" interval={0} tick={{ fontSize: 10 }} />
                          <YAxis allowDecimals={false} />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="Male" stackId="g" fill="#0088FE" />
                          <Bar dataKey="Female" stackId="g" fill="#FF6384" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div></div>
                </div>
                <div className="col-12 col-lg-6">
                  <div className="card h-100"><div className="card-body">
                    <div className="table-responsive" style={{ maxHeight: 380, overflowY: 'auto' }}>
                      <table className="table table-sm align-middle">
                        <thead><tr><th>Hour</th><th>Male</th><th>Female</th>{branches.map(b=> (<th key={b}>{b}</th>))}</tr></thead>
                        <tbody>
                          {perDateHourly[d].map((r, idx) => (
                            <tr key={idx}><td>{r.hourLabel}</td><td>{r.Male||0}</td><td>{r.Female||0}</td>{branches.map(b => (<td key={b}>{r[b]||0}</td>))}</tr>
                          ))}
                          {!perDateHourly[d].length && (<tr><td colSpan={3+branches.length} className="text-center text-muted">No records</td></tr>)}
                        </tbody>
                      </table>
                    </div>
                  </div></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  if (embedded) return content;

  return (
    <div className='wrapper'>
      <Navbar/>
      <Sidebar/>
      <main className="page-content customer-contnent">
        <div className="site-main-design">
          {content}
        </div>
      </main>
    </div>
  );
}


